import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import { entityService } from "../../services/entityService";

const log = createFileLogger('ENTITY_LIST_LINKS');

export const entityListLinksCommand: SelfContainedCommand = {
  id: 'entity_list_links',
  description: `Find all entities linked to a given entity.

**Use Cases:**
- Find all entities linked to a Vehicle
- Get all receipts for a task
- Find all tasks related to an account
- Discover entity relationships

**Filtering:**
- Filter by relationship type (e.g., only "hasReceipt" links)
- Filter by target entity type (e.g., only Document entities)
• Combine both filters`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Entity ID to query',
        required: true
      },
      {
        name: 'relationType',
        type: 'string',
        description: 'Filter by relationship type (optional)',
        required: false
      },
      {
        name: 'entityType',
        type: 'string',
        description: 'Filter by target entity type (optional)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          id: 'vehicle_123', 
          relationType: 'hasReceipt' 
        },
        expectedOutput: createSuccessResult('Found linked entities', {
          entities: [
            { _id: 'doc_456', name: 'Registration Receipt', entityType: 'Document' }
          ],
          count: 1
        }),
        description: 'Find all receipts for a vehicle',
        context: 'When querying entity relationships'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 300,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { id, relationType, entityType } = params;
    
    try {
      log.info('[entity_list_links] Finding linked entities:', { id, relationType, entityType });
      
      if (!id) {
        return createErrorResult(
          'Missing required parameter',
          'id is required',
          'validation',
          { data: { id, relationType, entityType } }
        );
      }
      
      // Query entity_edges for this entity
      const edges = await entityService.getEntityEdges(
        id,
        'both',  // Get both incoming and outgoing edges
        relationType
      );
      
      log.info('[entity_list_links] Found edges:', { count: edges.length });
      
      // Group edges by direction
      const outgoing = edges.filter((e: any) => e.from_id === id);
      const incoming = edges.filter((e: any) => e.to_id === id);
      
      log.info('[entity_list_links] ✅ Edges found:', { 
        total: edges.length,
        outgoing: outgoing.length,
        incoming: incoming.length
      });
      
      return createSuccessResult(
        `Found ${edges.length} relationship${edges.length === 1 ? '' : 's'}`,
        {
          edges: edges,
          outgoing: outgoing,
          incoming: incoming,
          count: edges.length,
          sourceId: id,
          relationType: relationType || 'all',
          entityType: entityType || 'all'
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityService.findLinked'
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[entity_list_links] Error:', error);
      return createErrorResult(
        'Failed to find linked entities',
        `Failed to query links: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { id, relationType, entityType },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

